import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { io, Socket } from 'socket.io-client';
import * as mediasoupClient from 'mediasoup-client';
import { firstValueFrom, skip, Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';

import { IDevice } from '../../models/be/device';
import { AuthService } from '../../../core/services/auth.service';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { LoggerService } from '../../../core/services/utils/logger.service';
import { isAccessTokenValid } from '../../utils/check.utils';
import { environment } from '../../../../environments/environment';
import { UsageTrackingService } from '../../../core/services/usage-tracking.service';
import { UsageSessionEndReason } from '../../models/be/usage-session';

export interface IStreamingDevice extends IDevice {
  sessionId?: string;
  stream?: MediaStream;
  consumer?: any;
  isStreamingLoading?: boolean;
  streamingUnavailable?: boolean;
  streamingTimeout?: any;
}

@Component({
  selector: 'streaming',
  templateUrl: './streaming.component.html',
  styleUrls: ['./streaming.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatGridListModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
})
export class StreamingComponent implements OnInit, AfterViewInit, OnDestroy {
  public formGroup!: FormGroup;
  public formArray!: FormArray;
  public hasError = false;
  public currentSensorIndex = 0;
  public fullScreenMode = false;
  public isSuperUser = false;
  public showDevStats = false;
  public streamStats: any = { fps: 0, bitrate: '0 kbps', resolution: '0x0', loss: 0 };

  @Input() devices: IStreamingDevice[] = [];
  @ViewChild('remoteVideo') remoteVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('streamViewport') streamViewport!: ElementRef<HTMLElement>;

  private readonly serverUrl = environment.sfuUrl;
  private readonly startupTimeoutMs = 15000;

  private socket?: Socket;
  private device?: mediasoupClient.Device;
  private consumerTransport: any;

  private foregroundSubscription?: Subscription;
  private statsInterval: any;
  private startupStatsInterval: any;
  private lastBytesReceived = 0;
  private lastStatsTimestamp = 0;
  private isStarting = false;
  private isDestroyed = false;
  private fullscreenOriginalParent?: Node;
  private fullscreenOriginalNextSibling?: ChildNode | null;
  private fullscreenOrientationRequestId = 0;
  private orientationLockedForFullscreen = false;
  private streamingUsageHandle?: string;
  private streamingUsageDeviceId?: string;

  constructor(
    private loggerService: LoggerService,
    private authService: AuthService,
    private requestsService: RequestsService,
    private usageTrackingService: UsageTrackingService
  ) {
    this.isSuperUser = this.requestsService.getCurrentUser()?.role === 'superuser';
  }

  ngOnInit(): void {
    this.foregroundSubscription = this.usageTrackingService.foreground$.pipe(
      skip(1)
    ).subscribe((isForeground) => {
      if (isForeground) {
        void this.startStreaming();
      } else {
        this.stopStreaming(false, 'app_background');
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.usageTrackingService.isInForeground()) {
      void this.startStreaming();
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.restoreFullscreenElement();
    this.foregroundSubscription?.unsubscribe();
    this.stopStreaming(false, 'navigation');
  }

  private get currentDevice(): IStreamingDevice | undefined {
    return this.devices[this.currentSensorIndex];
  }

  toggleFullScreen(): void {
    if (this.fullScreenMode) {
      this.restoreFullscreenElement();
      return;
    }

    const element = this.streamViewport?.nativeElement;

    if (!element) {
      this.fullScreenMode = true;
      return;
    }

    this.fullscreenOriginalParent = element.parentNode || undefined;
    this.fullscreenOriginalNextSibling = element.nextSibling;
    document.body.appendChild(element);
    document.body.classList.add('streaming-fullscreen-open');
    this.fullScreenMode = true;
    void this.lockLandscapeOrientation();
  }

  private restoreFullscreenElement(): void {
    const element = this.streamViewport?.nativeElement;
    const shouldUnlockOrientation = this.fullScreenMode || this.orientationLockedForFullscreen;

    this.fullscreenOrientationRequestId += 1;

    if (element && this.fullscreenOriginalParent) {
      this.fullscreenOriginalParent.insertBefore(element, this.fullscreenOriginalNextSibling || null);
    }

    document.body.classList.remove('streaming-fullscreen-open');
    this.fullscreenOriginalParent = undefined;
    this.fullscreenOriginalNextSibling = undefined;
    this.fullScreenMode = false;

    if (shouldUnlockOrientation) {
      this.orientationLockedForFullscreen = false;
      void this.unlockScreenOrientation();
    }
  }

  private async lockLandscapeOrientation(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const requestId = ++this.fullscreenOrientationRequestId;

    try {
      await ScreenOrientation.lock({ orientation: 'landscape' });

      if (requestId !== this.fullscreenOrientationRequestId || !this.fullScreenMode) {
        await ScreenOrientation.unlock();
        return;
      }

      this.orientationLockedForFullscreen = true;
    } catch (error) {
      this.loggerService.warn('Unable to lock streaming in landscape orientation', error as object);
    }
  }

  private async unlockScreenOrientation(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await ScreenOrientation.unlock();
    } catch (error) {
      this.loggerService.warn('Unable to restore automatic screen orientation', error as object);
    }
  }

  async startStreaming(): Promise<void> {
    if (this.isDestroyed || this.isStarting || !this.devices.length) {
      return;
    }

    this.isStarting = true;

    try {
      if (!isAccessTokenValid()) {
        await firstValueFrom(this.authService.callRefreshToken$());
      }
      if (this.isDestroyed) return;

      this.stopStreaming();

      this.resetDevicesState();
      await this.connectAndInitializeTransport();
      for (const dev of this.devices) {
        if (this.isDestroyed) return;
        this.prepareDeviceLoading(dev);
        await this.consumeDeviceStream(dev);
      }
    } catch (error) {
      if (this.isDestroyed) return;
      this.loggerService.error(`Errore avvio streaming: ${error}`);
      this.markCurrentDeviceUnavailable();
    } finally {
      this.isStarting = false;
    }
  }

  private async connectAndInitializeTransport(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const socket = io(this.serverUrl);
      this.socket = socket;
      let settled = false;

      const fail = (error: any) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      socket.on('disconnect', (reason: string) => {
        this.loggerService.warn(`❌ Socket SFU disconnesso: ${reason}`);
        fail(new Error('STREAMING_DISCONNECTED'));
      });

      socket.on('connect_error', (error: any) => {
        this.loggerService.error(`Errore connessione Socket SFU: ${error}`);
        fail(error);
      });

      socket.once('connect', async () => {
        if (this.isDestroyed) { socket.disconnect(); fail(new Error('STREAMING_CANCELLED')); return; }
        this.socket = socket;
        this.loggerService.info('✅ Connesso al server SFU Azure!');

        try {
          this.device = new mediasoupClient.Device();

          const routerRtpCapabilities = await this.emitAsync('getRouterRtpCapabilities');
          if (this.isDestroyed) { fail(new Error('STREAMING_CANCELLED')); return; }
          await this.device.load({ routerRtpCapabilities });
          if (this.isDestroyed) { fail(new Error('STREAMING_CANCELLED')); return; }

          const transportParams = await this.emitAsync('createTransport');
          if (this.isDestroyed) { fail(new Error('STREAMING_CANCELLED')); return; }
          if (transportParams?.error) {
            throw new Error(transportParams.error);
          }

          this.consumerTransport = this.device.createRecvTransport(transportParams);

          this.consumerTransport.on(
            'connect',
            ({ dtlsParameters }: any, callback: any, errback: any) => {
              this.socket?.emit('connectTransport', { dtlsParameters }, (response: any) => {
                if (response?.error) errback(response.error);
                else callback();
              });
            }
          );

          this.consumerTransport.on('connectionstatechange', (state: string) => {
            this.loggerService.info(`🧊 Stato Transport UDP: ${state}`);

            if (state === 'failed' || state === 'disconnected' || state === 'closed') {
              this.endStreamingUsage('stream_error');
              const current = this.currentDevice;
              if (current) {
                current.isStreamingLoading = false;
                current.streamingUnavailable = true;
              }
            }
          });

          if (!settled) {
            settled = true;
            resolve();
          }
        } catch (error) {
          fail(error);
        }
      });
    });
  }

  private async consumeDeviceStream(dev: IStreamingDevice): Promise<void> {
    const t0 = performance.now();
    const mark = (label: string, extra?: any) => {
      const suffix = extra ? ` ${JSON.stringify(extra)}` : '';
      this.loggerService.info(`[${dev.label}] ${label} +${Math.round(performance.now() - t0)}ms${suffix}`);
    };

    try {
      mark('consume:request');

      const consumeParams = await this.emitAsync('consume', {
        rtpCapabilities: this.device?.rtpCapabilities,
        sensorId: dev.label,
      });
      if (this.isDestroyed) return;

      mark('consume:response', { hasError: !!consumeParams?.error });

      if (consumeParams?.error) {
        this.loggerService.error(`Streaming non disponibile per ${dev.label}: ${consumeParams.error}`);
        this.markDeviceUnavailable(dev, 'consume_error');
        return;
      }

      const consumer = await this.consumerTransport.consume({
        id: consumeParams.id,
        producerId: consumeParams.producerId,
        kind: consumeParams.kind,
        rtpParameters: consumeParams.rtpParameters,
      });
      if (this.isDestroyed) { consumer.close(); return; }
      dev.consumer = consumer;

      mark('transport.consume:done');

      dev.stream = new MediaStream([dev.consumer.track]);
      this.bindConsumerTrackEvents(dev, mark);

      if (this.currentDevice === dev) {
        await this.attachAndPlayCurrentVideo(dev);
      }
      if (this.isDestroyed) return;

      mark('resume:request');
      const resumeResponse = await this.emitAsync('resume', { consumerId: dev.consumer.id });
      if (this.isDestroyed) return;
      mark('resume:response', resumeResponse);

      this.startStartupStatsPolling(dev, dev.consumer);
    } catch (error) {
      if (this.isDestroyed) return;
      this.loggerService.error(`Errore Consumer per ${dev.label}: ${error}`);
      this.markDeviceUnavailable(dev, 'consumer_exception');
    }
  }

  private bindConsumerTrackEvents(dev: IStreamingDevice, mark: (label: string, extra?: any) => void): void {
    if (!dev.consumer?.track) {
      return;
    }

    dev.consumer.track.onmute = () => mark('track:mute');

    dev.consumer.track.onunmute = () => {
      mark('track:onunmute');
      this.markDeviceReady(dev, 'track.onunmute');
    };

    dev.consumer.track.onended = () => {
      mark('track:ended');
      dev.isStreamingLoading = false;
      dev.streamingUnavailable = true;
      if (this.currentDevice === dev) {
        this.endStreamingUsage('stream_error');
      }
    };
  }

  private async attachAndPlayCurrentVideo(dev: IStreamingDevice): Promise<void> {
    const video = this.remoteVideo?.nativeElement;
    if (!video || !dev.stream) {
      return;
    }

    this.bindVideoElementEvents(dev, video);

    video.pause();
    video.srcObject = dev.stream;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;

    this.loggerService.info(`[${dev.label}] video:srcObject assigned`);

    try {
      this.loggerService.info(`[${dev.label}] video:play() request`);
      await video.play();
      this.loggerService.info(`[${dev.label}] video:play() resolved`);
    } catch (error) {
      this.loggerService.error(`[${dev.label}] video:play() failed ${error}`);
    }
  }

  private bindVideoElementEvents(dev: IStreamingDevice, video: HTMLVideoElement): void {
    video.onloadedmetadata = () => {
      this.loggerService.info(
        `[${dev.label}] video:loadedmetadata readyState=${video.readyState} paused=${video.paused}`
      );
    };

    video.onloadeddata = () => {
      this.loggerService.info(
        `[${dev.label}] video:loadeddata readyState=${video.readyState} paused=${video.paused}`
      );
      this.markDeviceReady(dev, 'loadeddata');
    };

    video.oncanplay = () => {
      this.loggerService.info(
        `[${dev.label}] video:canplay readyState=${video.readyState} paused=${video.paused}`
      );
    };

    video.onplaying = () => {
      this.loggerService.info(
        `[${dev.label}] video:playing readyState=${video.readyState} paused=${video.paused}`
      );
      this.markDeviceReady(dev, 'playing');
    };

    video.onwaiting = () => {
      this.loggerService.warn(
        `[${dev.label}] video:waiting readyState=${video.readyState} currentTime=${video.currentTime}`
      );
    };

    video.onstalled = () => {
      this.loggerService.warn(
        `[${dev.label}] video:stalled readyState=${video.readyState} currentTime=${video.currentTime}`
      );
    };

    video.onerror = () => {
      this.loggerService.error(
        `[${dev.label}] video:error code=${video.error?.code ?? 'unknown'}`
      );
    };
  }

  private startStartupStatsPolling(dev: IStreamingDevice, consumer: any): void {
    if (this.startupStatsInterval) {
      clearInterval(this.startupStatsInterval);
    }

    const startedAt = performance.now();

    this.startupStatsInterval = setInterval(async () => {
      if (!dev.consumer || dev.consumer !== consumer) {
        clearInterval(this.startupStatsInterval);
        return;
      }

      try {
        const stats = await consumer.getStats();

        stats.forEach((report: any) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            if ((report.framesDecoded ?? 0) > 0) {
              this.markDeviceReady(dev, 'framesDecoded');
            }
          }
        });
      } catch (error) {
        this.loggerService.warn(`[${dev.label}] startup stats error: ${error}`);
      }
    }, 500);

    setTimeout(() => {
      if (this.startupStatsInterval) {
        clearInterval(this.startupStatsInterval);
        this.startupStatsInterval = undefined;
      }
    }, 20000);
  }

  private markDeviceReady(dev: IStreamingDevice, source: string): void {
    if (!dev.isStreamingLoading) {
      return;
    }

    if (dev.streamingTimeout) {
      clearTimeout(dev.streamingTimeout);
      dev.streamingTimeout = undefined;
    }

    dev.isStreamingLoading = false;
    dev.streamingUnavailable = false;

    this.loggerService.info(`[${dev.label}] ready via ${source}`);

    if (this.currentDevice === dev && dev.consumer) {
      this.startStatsPolling(dev.consumer);
      this.startStreamingUsage(dev);
    }
  }


  private markDeviceUnavailable(dev: IStreamingDevice, source: string): void {
    if (dev.streamingTimeout) {
      clearTimeout(dev.streamingTimeout);
      dev.streamingTimeout = undefined;
    }

    dev.isStreamingLoading = false;
    dev.streamingUnavailable = true;
    this.loggerService.error(`[${dev.label}] streaming unavailable via ${source}`);
    if (this.currentDevice === dev) {
      this.endStreamingUsage('stream_error');
    }
  }

  private prepareDeviceLoading(dev: IStreamingDevice): void {
    dev.isStreamingLoading = true;
    dev.streamingUnavailable = false;

    if (dev.streamingTimeout) {
      clearTimeout(dev.streamingTimeout);
    }

    dev.streamingTimeout = setTimeout(() => {
      if (dev.isStreamingLoading) {
        this.markDeviceUnavailable(dev, 'startup_timeout');
      }
    }, this.startupTimeoutMs);
  }

  private resetDevicesState(): void {
    this.devices.forEach((dev) => {
      if (dev.streamingTimeout) {
        clearTimeout(dev.streamingTimeout);
        dev.streamingTimeout = undefined;
      }

      dev.isStreamingLoading = false;
      dev.streamingUnavailable = false;
    });
  }

  private markCurrentDeviceUnavailable(): void {
    const current = this.currentDevice;
    if (current) {
      this.markDeviceUnavailable(current, 'generic_error');
    }
  }

  private emitAsync(event: string, data?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error(`Socket non inizializzato per evento ${event}`));
        return;
      }

      if (data !== undefined) {
        this.socket.emit(event, data, (response: any) => resolve(response));
      } else {
        this.socket.emit(event, (response: any) => resolve(response));
      }
    });
  }

  private startStatsPolling(consumer: any): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }

    this.lastBytesReceived = 0;
    this.lastStatsTimestamp = 0;

    this.statsInterval = setInterval(async () => {
      if (!consumer) {
        return;
      }

      try {
        const stats = await consumer.getStats();

        stats.forEach((report: any) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const now = report.timestamp;
            const bytes = report.bytesReceived;
            let bitrate = 0;

            if (this.lastStatsTimestamp && this.lastBytesReceived) {
              bitrate = (8 * (bytes - this.lastBytesReceived)) / (now - this.lastStatsTimestamp);
            }

            this.lastBytesReceived = bytes;
            this.lastStatsTimestamp = now;

            this.streamStats = {
              resolution: `${report.frameWidth || 0}x${report.frameHeight || 0}`,
              fps: report.framesPerSecond || 0,
              bitrate: Math.round(bitrate) + ' kbps',
              loss: report.packetsLost || 0,
            };
          }
        });
      } catch (error) {
        console.warn('Errore lettura statistiche WebRTC:', error);
      }
    }, 1000);
  }

  nextSensor(): void {
    if (this.currentSensorIndex < this.devices.length - 1) {
      this.stopStreaming(false, 'stream_switch');
      this.currentSensorIndex++;
      void this.startStreaming();
    }
  }

  prevSensor(): void {
    if (this.currentSensorIndex > 0) {
      this.stopStreaming(false, 'stream_switch');
      this.currentSensorIndex--;
      void this.startStreaming();
    }
  }

  stopStreaming(restart = false, trackingReason: UsageSessionEndReason = 'stream_stopped'): void {
    this.endStreamingUsage(trackingReason);
    if (this.startupStatsInterval) {
      clearInterval(this.startupStatsInterval);
      this.startupStatsInterval = undefined;
    }

    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = undefined;
    }

    this.lastBytesReceived = 0;
    this.lastStatsTimestamp = 0;

    const video = this.remoteVideo?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.oncanplay = null;
      video.onplaying = null;
      video.onwaiting = null;
      video.onstalled = null;
      video.onerror = null;
    }

    this.devices.forEach((device) => {
      if (device.streamingTimeout) {
        clearTimeout(device.streamingTimeout);
        device.streamingTimeout = undefined;
      }

      if (device.consumer) {
        try {
          device.consumer.close();
        } catch { }
        device.consumer = undefined;
      }

      device.stream = undefined;
      device.isStreamingLoading = false;
    });

    if (this.consumerTransport) {
      try {
        this.consumerTransport.close();
      } catch { }
      this.consumerTransport = undefined;
    }

    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch { }
      this.socket = undefined;
    }

    this.device = undefined;

    if (restart) {
      setTimeout(() => void this.startStreaming(), 0);
    }
  }

  private startStreamingUsage(device: IStreamingDevice): void {
    if (this.streamingUsageHandle && this.streamingUsageDeviceId === device.id) {
      return;
    }

    this.endStreamingUsage('stream_switch');
    this.streamingUsageHandle = this.usageTrackingService.beginStreamingSession(device.id, {
      deviceLabel: device.label,
    });
    this.streamingUsageDeviceId = this.streamingUsageHandle ? device.id : undefined;
  }

  private endStreamingUsage(reason: UsageSessionEndReason): void {
    if (!this.streamingUsageHandle) {
      return;
    }

    const handle = this.streamingUsageHandle;
    this.streamingUsageHandle = undefined;
    this.streamingUsageDeviceId = undefined;
    void this.usageTrackingService.endStreamingSession(handle, reason);
  }
}
